import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import AppTextField from "../../../../../../components/common/forms/AppTextField";
import { useESRApprovalContext } from "../../Context/ESRApprovalContext";
import { useGetMechanicQuery } from "../../../../../../hooks/useMechanic";
import { ESRManhourInitialValues, ESRManhourSchema } from "./utils/schema";
import { ESRManpowerColumns, ESRManhourFormFields } from "./utils/types";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { appColors } from "../../../../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackBar } from "../../../../../../components/ToastProvider";
import AppDialog from "../../../../../../components/common/AppDialog";
import AppSelect from "../../../../../../components/common/forms/AppSelect";

const ESRManhourForm = () => {
  const { showSnackBar } = useSnackBar();
  const {
    openManhourDialog,
    setOpenManhourDialog,
    esrManpowerArray,
    setEsrManpowerArray,
    currentManhourSelected,
    setCurrentManhourSelected,
    mHActionType,
    setMhActionType,
  } = useESRApprovalContext();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<ESRManhourFormFields>({
    defaultValues: ESRManhourInitialValues,
    mode: "onChange",
    resolver: yupResolver(ESRManhourSchema),
  });

  const mechanics = useGetMechanicQuery();
  const mechanicOptions: any[] = useMemo(() => {
    if (mechanics?.data === undefined) {
      return [];
    }
    const mechanicOptionsArray = mechanics.data
      .map((e) => {
        return {
          id: e.id,
          label: e.name,
          value: e.id,
        };
      })
      .filter((option) => option !== null)
      .sort((a, b) => {
        const labelA = a?.label ?? "";
        const labelB = b?.label ?? "";
        return labelA.localeCompare(labelB.toString());
      });

    return mechanicOptionsArray;
  }, [mechanics]);

  const handleClose = () => {
    setValue("manHours", 0);
    setValue("mechanicName", "");
    setCurrentManhourSelected({
      id: "",
      manHours: 0,
      mechanicId: "",
      mechanicName: "",
    });
    reset();
    setOpenManhourDialog(false);
  };

  const onSubmit = (formData: ESRManhourFormFields) => {
    const number = esrManpowerArray.length + 1;
    const mechanic = mechanicOptions.find(
      (mech) => formData.mechanicName == mech.id
    );

    const addToArray = () => {
      const newMh: ESRManpowerColumns = {
        id: number.toString(),
        manHours: formData.manHours,
        mechanicName: mechanic.label,
        mechanicId: mechanic.id,
      };

      setEsrManpowerArray((prev: ESRManpowerColumns[]) => {
        return [...prev, newMh];
      });

      handleClose();
    };

    const updateArray = (index: number, newValue: ESRManpowerColumns) => {
      const newArray: ESRManpowerColumns[] = [...esrManpowerArray];

      newArray[index] = newValue;

      setEsrManpowerArray(newArray);
      setCurrentManhourSelected({
        id: "",
        manHours: 0,
        mechanicName: "",
        mechanicId: "",
      });
      handleClose();
    };

    if (mHActionType == "new") {
      addToArray();
    }

    if (mHActionType == "update") {
      const newMh: ESRManpowerColumns = {
        id: currentManhourSelected.id,
        mechanicName: mechanic.label,
        mechanicId: mechanic.id,
        manHours: formData.manHours,
      };

      esrManpowerArray.forEach((mh, index) => {
        if (mh.id == newMh.id) {
          updateArray(index, newMh);
        }
      });
    }
  };

  useEffect(() => {
    if (!currentManhourSelected) {
      return;
    } else {
      setValue("manHours", currentManhourSelected.manHours);
      setValue("mechanicName", currentManhourSelected.mechanicId);
      setValue("mechanicId", currentManhourSelected.mechanicId);
    }
  }, [currentManhourSelected]);

  return (
    <AppDialog
      open={openManhourDialog}
      onDialogClose={handleClose}
      title={"Manpower and Manhour"}
      maxWidth="sm"
      sx={{ textAlign: "center" }}
    >
      <Stack
        component="form"
        height="100%"
        bgcolor="white"
        py={1}
        borderRadius={1}
      >
        <Box
          height="100%"
          overflow="auto"
          bgcolor="white"
          gap={2}
          sx={{ paddingTop: ".50em" }}
        >
          <Controller
            control={control}
            name="mechanicName"
            render={({ field }) => (
              <AppSelect
                label="Manpower"
                labelAlignment="right"
                labelWidth="20%"
                fieldWidth="80%"
                fontWeight={400}
                placeholder="Select here"
                isOptionsNotSorted={true}
                error={!!errors.mechanicName}
                helperText={errors.mechanicName?.message}
                options={mechanicOptions}
                {...register("mechanicName")}
                {...field}
              />
            )}
          />
          <Box sx={{ marginTop: "1em" }}>
            <AppTextField
              label="Man Hours"
              fontWeight={400}
              labelAlignment="right"
              labelWidth="20%"
              fieldWidth="80%"
              error={!!errors.manHours}
              helperText={errors.manHours?.message}
              {...register("manHours")}
            />
          </Box>
          <div style={{ display: "none" }}>
            <AppTextField
              label="mechanicId"
              required={true}
              error={!!errors.mechanicId}
              helperText={errors.mechanicId?.message}
              hidden={true}
              {...register("mechanicId")}
            />
          </div>
        </Box>
        <Box sx={{ width: "100%", display: "flex" }}>
          <Button
            onClick={handleSubmit(onSubmit)}
            sx={{ width: 150, marginLeft: "auto", marginTop: "1.5em" }}
          >
            Save
          </Button>
        </Box>
      </Stack>
    </AppDialog>
  );
};

export default ESRManhourForm;
